import styled from '@emotion/styled';
import { BodyXXSmall } from '@shared/components/core/text/Typography';
import {
    getTinyVariantBackground,
    getTinyVariantColor,
    getTinyVariantHoverBackground,
    getTinyVariantHoverColor,
    TinyVariant,
} from '@shared/constants/components/tinyUtils';
import { borderRadius, TBorderRadius } from '@shared/constants/Styles';

export const TinyRow = styled.div<{ $variant: TinyVariant }>`
    display: flex;
    flex-direction: row;
    align-items: center;
    gap: 0.25rem;
    border-radius: 0.25rem;
    /* The diminished variants are unfilled, so padding would only push them out of line with their row. */
    padding: ${(p) => (p.$variant.startsWith('diminished') ? '0' : '0.1rem 0.4rem')};
    color: ${(p) => getTinyVariantColor(p.$variant, p.theme) || p.theme.color.text};
    background-color: ${(p) => getTinyVariantBackground(p.$variant, p.theme) || 'transparent'};
    /* The one variant that reads as a link rather than a chip — the underline is what marks it clickable. */
    text-decoration: ${(p) => (p.$variant.startsWith('underline') ? 'underline' : 'none')};
`;

export const TinyText = styled(BodyXXSmall)`
    color: inherit;
`;

export const BoldTinyText = styled(BodyXXSmall)`
    font-weight: 700;
    color: inherit;
`;

/**
 * The interactive form of {@link TinyRow}, rendered as a `button` or `a` by TinyButton. The resets keep a
 * real button/anchor looking identical to the static row, and hover/focus inverts the variant's color and
 * background — see getTinyVariantHoverColor for how the unfilled (diminished) variants, which have no
 * background to swap in, get their inverted text color.
 *
 * The hover/focus inversion is gated on `:not(:disabled)`: the `disabled` attribute already makes a real
 * button inert, but without this a disabled row still lit up under the pointer and read as pressable. The
 * dimming matches {@link TinyIconButtonBase} so both halves of the family disable the same way. `:disabled`
 * never matches the anchor/NavLink forms, which can't be disabled, so their hover is untouched.
 */
export const TinyButtonRow = styled(TinyRow)<{ $variant: TinyVariant; $borderRadius?: TBorderRadius }>`
    padding: 0.1rem 0.4rem;
    cursor: pointer;
    user-select: none;
    border: none;
    font: inherit;
    text-align: left;
    border-radius: ${(p) => (p.$borderRadius ? borderRadius[p.$borderRadius] : '0.25rem')};
    min-height: ${(p) => (p.theme.scaleSize >= 20 ? '44px' : '24px')};
    &:hover:not(:disabled),
    &:focus-visible:not(:disabled) {
        background-color: ${(p) => getTinyVariantHoverBackground(p.$variant, p.theme)};
        color: ${(p) => getTinyVariantHoverColor(p.$variant, p.theme)};
    }

    &:disabled {
        cursor: default;
        opacity: 0.45;
    }
`;

export const TinyButtonWrapper = styled.div`
    display: flex;
    flex-direction: row;
    align-items: center;
    gap: 0.5rem;
`;
